import { useEffect, useRef, useState } from "react"
import API from "../../api"
import { searchIcon } from "../../icons"
import { daggerNavigate } from "../../utils/navigate.util"
import "./spotlight.component.css"

const DEBOUNCE_MS = 300

const SpotlightComponent = ({ router }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [results, setResults] = useState({ pages: [], caseStudies: [] })
  const [isLoading, setIsLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef()
  const debounceRef = useRef()
  const resultsRef = useRef()

  // Cmd+K / Ctrl+K global listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault()
        setIsOpen((prev) => !prev)
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  // Listen for header button event
  useEffect(() => {
    const handleOpen = () => setIsOpen(true)
    document.addEventListener("EVENTS.OPEN_SPOTLIGHT", handleOpen)
    return () => document.removeEventListener("EVENTS.OPEN_SPOTLIGHT", handleOpen)
  }, [])

  // Focus input on open, reset on close, body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
      setTimeout(() => inputRef.current?.focus(), 50)
    } else {
      document.body.style.overflow = ""
      setQuery("")
      setResults({ pages: [], caseStudies: [] })
      setActiveIndex(0)
    }
  }, [isOpen])

  // Debounced search
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults({ pages: [], caseStudies: [] })
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      performSearch(query.trim())
    }, DEBOUNCE_MS)

    return () => clearTimeout(debounceRef.current)
  }, [query])

  // Scroll active result into view
  useEffect(() => {
    if (!resultsRef.current) return
    const activeEl = resultsRef.current.querySelector(
      ".dagger-spotlight-result.active",
    )
    if (activeEl) {
      activeEl.scrollIntoView({ block: "nearest" })
    }
  }, [activeIndex])

  const performSearch = async (searchTerm) => {
    try {
      const [pages, caseStudies] = await Promise.all([
        API.getContent("pages", null, {
          "filters[$or][0][Title][$containsi]": searchTerm,
          "filters[$or][1][MetaDescription][$containsi]": searchTerm,
          "fields[0]": "Title",
          "fields[1]": "Slug",
          "fields[2]": "MetaDescription",
          "fields[3]": "Environment",
        }),
        API.getContent("case-studies", null, {
          "filters[$or][0][Title][$containsi]": searchTerm,
          "filters[$or][1][Client][$containsi]": searchTerm,
          "filters[$or][2][MetaDescription][$containsi]": searchTerm,
          "fields[0]": "Title",
          "fields[1]": "Slug",
          "fields[2]": "Client",
          "fields[3]": "MetaDescription",
          "fields[4]": "Environment",
          "populate[0]": "Category",
        }),
      ])

      setResults({ pages, caseStudies })
      setActiveIndex(0)
    } catch (e) {
      console.error("Search error:", e)
    } finally {
      setIsLoading(false)
    }
  }

  // Build flat list for keyboard navigation
  const flatResults = [
    ...results.pages.filter(Boolean).map((p) => ({
      type: "page",
      title: p.attributes.Title,
      subtitle: p.attributes.MetaDescription || "",
      path: p.attributes.Slug === "home" ? "/" : `/${p.attributes.Slug}`,
    })),
    ...results.caseStudies.filter(Boolean).map((cs) => ({
      type: "case-study",
      title: cs.attributes.Title,
      subtitle:
        cs.attributes.Client +
        (cs.attributes.Category?.data
          ? ` · ${cs.attributes.Category.data.attributes.Category}`
          : ""),
      path: `/study/${cs.attributes.Slug}`,
    })),
  ]

  const navigateToResult = (result) => {
    setIsOpen(false)
    if (result.path === window.location.pathname) return
    daggerNavigate(() => {
      router.navigate(result.path)
    })
  }

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "Escape":
        setIsOpen(false)
        break
      case "ArrowDown":
        e.preventDefault()
        setActiveIndex((prev) => Math.min(prev + 1, flatResults.length - 1))
        break
      case "ArrowUp":
        e.preventDefault()
        setActiveIndex((prev) => Math.max(prev - 1, 0))
        break
      case "Enter":
        e.preventDefault()
        if (flatResults[activeIndex]) {
          navigateToResult(flatResults[activeIndex])
        }
        break
      default:
        break
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setIsOpen(false)
  }

  if (!isOpen) return null

  // Compute index offset for case studies group
  const pageCount = results.pages.length

  return (
    <div className="dagger-spotlight-backdrop" onClick={handleBackdropClick}>
      <div className="dagger-spotlight-panel">
        <div className="dagger-spotlight-input-row">
          {searchIcon}
          <input
            ref={inputRef}
            type="text"
            placeholder="Search pages and work..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="dagger-spotlight-input"
            aria-label="Search"
          />
          <kbd className="dagger-spotlight-kbd">ESC</kbd>
        </div>

        {flatResults.length > 0 && (
          <div className="dagger-spotlight-results" ref={resultsRef}>
            {results.pages.length > 0 && (
              <div className="dagger-spotlight-group">
                <h6 className="dagger-spotlight-group-label">Pages</h6>
                {results.pages.filter(Boolean).map((p, i) => (
                  <div
                    key={p.id}
                    className={`dagger-spotlight-result ${activeIndex === i ? "active" : ""}`}
                    onClick={() => navigateToResult(flatResults[i])}
                    onMouseEnter={() => setActiveIndex(i)}
                  >
                    <span className="dagger-spotlight-result-title">
                      {p.attributes.Title}
                    </span>
                    {p.attributes.MetaDescription && (
                      <span className="dagger-spotlight-result-subtitle">
                        {p.attributes.MetaDescription}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {results.caseStudies.length > 0 && (
              <div className="dagger-spotlight-group">
                <h6 className="dagger-spotlight-group-label">Case Studies</h6>
                {results.caseStudies.filter(Boolean).map((cs, i) => {
                  const flatIndex = pageCount + i
                  return (
                    <div
                      key={cs.id}
                      className={`dagger-spotlight-result ${activeIndex === flatIndex ? "active" : ""}`}
                      onClick={() => navigateToResult(flatResults[flatIndex])}
                      onMouseEnter={() => setActiveIndex(flatIndex)}
                    >
                      <span className="dagger-spotlight-result-title">
                        {cs.attributes.Title}
                      </span>
                      <span className="dagger-spotlight-result-subtitle">
                        {cs.attributes.Client}
                        {cs.attributes.Category?.data &&
                          ` · ${cs.attributes.Category.data.attributes.Category}`}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {query.length >= 2 && flatResults.length === 0 && !isLoading && (
          <div className="dagger-spotlight-empty">No results found.</div>
        )}

        {isLoading && (
          <div className="dagger-spotlight-loading">Searching...</div>
        )}
      </div>
    </div>
  )
}

export default SpotlightComponent
